<template>
  <div class="card-actions action-deal-signed">
    <!-- eslint-disable vue/no-v-html -->
    <div
      class="action-deal-signed__intro"
      v-html="trans('deal_dashboard.action.signed.intro', { name: interlocutor.firstname })"
    />
    <div
      ref="actionContent"
      class="card-actions__content action-deal-content"
    >
      <div class="action-deal-content__fieldset">
        <p class="input-label">
          {{ trans('messaging.actions.signed.deal_amount') }}
        </p>
        <div class="input-text input-text--legend input-icon">
          <input
            v-model.trim="amount"
            type="number"
            name="amount"
          >
          <i>
            {{ trans('global.price_tax') }}&nbsp;{{ trans('global.currency_symbol') }}
          </i>
        </div>
      </div>

      <div class="datepicker action-deal-content__fieldset">
        <p class="input-label">
          {{ trans('messaging.actions.signed.deal_start_date') }}
        </p>
        <vue-date-picker
          v-model="dealSignedDate"
          name="start_date"
          :format="displayFormat"
          locale="fr"
          :enable-time-picker="false"
          auto-apply
          @opened="onDatepickerDisplay(true)"
          @closed="onDatepickerDisplay(false)"
        >
          <template #input-icon>
            <span class="datepicker__icon datepicker__icon--datepicker" />
          </template>
        </vue-date-picker>
      </div>

      <div class="datepicker action-deal-content__fieldset">
        <p class="input-label">
          {{ trans('messaging.actions.signed.deal_end_date') }}
        </p>
        <vue-date-picker
          v-model="dealEndDate"
          class="datepicker"
          :input-class="{ disabled: unlimited }"
          name="end_date"
          :format="displayFormat"
          :min-date="disabledEndDates"
          locale="fr"
          :enable-time-picker="false"
          auto-apply
          @opened="onDatepickerDisplay(true)"
          @closed="onDatepickerDisplay(false)"
          @update:model-value="onCalendarChange"
        >
          <template #input-icon>
            <span class="datepicker__icon datepicker__icon--datepicker" />
          </template>
        </vue-date-picker>
        <div class="input-checkbox active">
          <input
            v-model="unlimited"
            type="checkbox"
            name="unlimited"
          >
          <label @click="unlimited = !unlimited">
            {{ trans('messaging.actions.signed.deal_duration_undetermined') }}
          </label>
        </div>
      </div>
    </div>
    <div class="card-actions__cta card-actions__cta--bottom">
      <button class="btn btn--secondary btn--cancel" @click="onCancelClick">
        {{ trans('deal_dashboard.action.signed.cancel') }}
      </button>
      <button
        class="btn btn--primary"
        :class="{ 'loading-btn': loading }"
        :disabled="isSubmitDisabled"
        @click="submit"
      >
        <span v-if="!loading">
          {{ trans('deal_dashboard.action.signed.cta') }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import VueDatePicker from '@vuepic/vue-datepicker';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { URL_XHR_DEAL_STATE_SIGNED } from '@tools/routes/routes';
import { Logger } from '@tools/logger/logger';
import { requestsMixin } from '@tools/requests-mixin';
import { parseDateToString } from '@tools/date';
import { actionsMixins } from './actions-mixins';
import { EVENTS } from './constants';

export default {
  components: {
    VueDatePicker,
  },
  mixins: [ actionsMixins, requestsMixin ],
  props: {
    interlocutor: { type: Object, required: true },
  },
  data() {
    return {
      title: this.trans('deal_dashboard.action.signed.title'),
      loading: false,
      displayFormat: 'dd/MM/yyyy',
      amount: 0,
      unlimited: false,
      dealSignedDate: null,
      dealEndDate: null,
    };
  },
  computed: {
    isSubmitDisabled() {
      // Should have an amount
      if (this.amount < 1 || !this.amount) {
        return true;
      }
      // Should have a start date
      if (this.dealSignedDate === null) {
        return true;
      }
      // Should have an end date if not unlimited
      if (!this.unlimited && this.dealEndDate === null) {
        return true;
      }
      // Dates should be in right order
      if (
        !this.unlimited
        && moment(this.dealSignedDate).isAfter(moment(this.dealEndDate))
      ) {
        return true;
      }
      return this.loading;
    },
    disabledEndDates() {
      return moment(this.dealSignedDate).toDate();
    },
  },
  watch: {
    unlimited(newValue) {
      if (newValue) {
        this.dealEndDate = null;
      }
    },
  },
  mounted() {
    this.$emit(EVENTS.TITLE_UPDATE, this.title);
    this.unlimited = this.deal.quantity === -1;
    this.amount = this.deal.amount;
    if (this.deal.start_date) {
      this.dealSignedDate = new Date(this.deal.start_date.iso);
    }
    if (this.deal.end_date) {
      this.dealEndDate = new Date(this.deal.end_date.iso);
    }
  },
  methods: {
    onCalendarChange(value) {
      if (value) {
        this.unlimited = false;
      }
    },
    onCancelClick() {
      this.$emit(EVENTS.ACTION_CANCELLED);
    },
    onDatepickerDisplay(force) {
      const flag = force || this.isSubmitDisabled;
      this.$refs.actionContent.classList.toggle('give-datepicker-some-space', flag);
      this.$refs.actionContent.classList.toggle('remove-datepicker-space', !flag);
    },
    async submit() {
      this.loading = true;
      let response = null;
      try {
        response = await this.post(
          URL_XHR_DEAL_STATE_SIGNED.replace('{id}', this.deal.id),
          {
            amount: this.amount,
            start_date: parseDateToString(this.dealSignedDate),
            end_date: this.dealEndDate === null ? null : parseDateToString(this.dealEndDate),
            unlimited: this.unlimited,
          },
        );
      } catch (error) {
        Logger.error(error);
      }
      this.loading = false;
      if (response && response.status === 200) {
        flashBagSuccess(this.trans('deal_dashboard.action.success'));
        this.$emit(EVENTS.ACTION_PERFORMED);
        return;
      }
      flashBagError(this.trans('deal_dashboard.action.failure'));
    },
  },
};
</script>
